/**
 * OAuth 2.0 Consent screen — Phase 6 (Q5 = single-page).
 *
 * Linear/Manus aesthetic: monochrome, no emoji, narrow column, generous
 * whitespace, brand purple only on the primary CTA. Mobile-friendly.
 *
 * Flow:
 *   1. /oauth/authorize on the backend validates the request and redirects
 *      here with `?request_token=<jwt>&client_name=<name>&scopes=<space-separated>`.
 *   2. We fetch the user's workspaces (requires the user to be logged in;
 *      if not, we redirect to /login with the consent URL as `?next=`).
 *   3. User picks a workspace (if more than one), reviews scopes, clicks
 *      Approve or Deny.
 *   4. We POST to /oauth/authorize/decision; the backend returns a
 *      `redirect_to` URL we navigate to (back to the third-party client).
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { API_URL } from '../../config/api';
import { getAuthToken } from '../../services/api/core';
import { AuthFormCard, AuthPageLayout } from '@/layouts/AuthPageLayout';

interface WorkspaceOption {
  id: string;
  name: string;
  role?: string;
}

interface DecisionResponse {
  redirect_to: string;
}

type Status = 'loading' | 'ready' | 'submitting' | 'done' | 'error';

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  read: 'Read customers, orders, products, campaigns, flows, and analytics.',
  write: 'Read + create or update campaigns, customer notes, flows, and segments. Cannot send.',
  execute: 'All of read + write, plus send campaigns, trigger flows, and post messages.',
  admin: 'Manage billing, API keys, team members, and workspace settings.',
};

const Consent: React.FC = () => {
  const [params] = useSearchParams();
  const requestToken = params.get('request_token') ?? '';
  const clientName = params.get('client_name') ?? 'an application';
  const scopesParam = params.get('scopes') ?? 'read';
  const requestedScopes = React.useMemo(
    () => scopesParam.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [scopesParam],
  );

  const [status, setStatus] = React.useState<Status>('loading');
  const [error, setError] = React.useState<string | null>(null);
  const [workspaces, setWorkspaces] = React.useState<WorkspaceOption[]>([]);
  const [workspaceId, setWorkspaceId] = React.useState<string | null>(null);
  const [grantedScopes, setGrantedScopes] = React.useState<Set<string>>(
    () => new Set(requestedScopes),
  );

  // Fetch workspace list once; if 401, route to login with a continue link.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!requestToken) {
        setStatus('error');
        setError('Missing request_token in URL. Did you arrive here from a valid /oauth/authorize call?');
        return;
      }
      try {
        const data = await apiClient.get<{ success?: boolean; data?: WorkspaceOption[] } | WorkspaceOption[]>(
          '/workspaces',
        );
        const list: WorkspaceOption[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        if (cancelled) return;
        if (list.length === 0) {
          setStatus('error');
          setError('You don\'t have any workspaces yet. Create one before connecting third-party apps.');
          return;
        }
        setWorkspaces(list);
        setWorkspaceId(list[0]?.id ?? null);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        if (/401|unauthorized/i.test(message)) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.assign(`/login?next=${next}`);
          return;
        }
        setStatus('error');
        setError(`Could not load workspaces: ${message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestToken]);

  const submit = async (decision: 'approve' | 'deny') => {
    setStatus('submitting');
    setError(null);
    try {
      const body =
        decision === 'approve'
          ? {
              request_token: requestToken,
              decision,
              workspaceId,
              grantedScopes: Array.from(grantedScopes),
            }
          : { request_token: requestToken, decision };
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/oauth/authorize/decision`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const result = (await res.json()) as DecisionResponse & { error?: string; error_description?: string };
      if (!res.ok) {
        throw new Error(result.error_description ?? result.error ?? `HTTP ${res.status}`);
      }
      if (!result.redirect_to) {
        throw new Error('Server did not return a redirect_to URL');
      }
      setStatus('done');
      window.location.assign(result.redirect_to);
    } catch (err) {
      setStatus('ready');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (status === 'loading') {
    return (
      <AuthPageLayout className="bg-white">
        <div className="text-center text-sm text-gray-500">Loading...</div>
      </AuthPageLayout>
    );
  }

  // Top-aligned, not centered: the cookie banner decides where to dock from
  // where Approve sits BEFORE its bottom padding lands, and a centered column
  // only moves up once that padding exists. Top-aligned, Approve's position is
  // the same either way, so on a 1440x789 laptop it stays clear of the banner.
  return (
    <AuthPageLayout className="items-start bg-white">
      <AuthFormCard className="p-6 shadow-none">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Connect {clientName}
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            {clientName} is requesting access to your BuzzBit X workspace.
          </p>

          {error && (
            <div className="mb-6 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-800">
              {error}
            </div>
          )}

          {workspaces.length > 1 && (
            <section className="mb-6">
              <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                Workspace
              </h2>
              <div className="space-y-2">
                {workspaces.map((w) => (
                  <label
                    key={w.id}
                    className="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-md cursor-pointer hover:border-gray-300"
                  >
                    <input
                      type="radio"
                      name="workspace"
                      value={w.id}
                      checked={workspaceId === w.id}
                      onChange={() => setWorkspaceId(w.id)}
                      className="text-gray-900 focus:ring-gray-300"
                    />
                    <span className="flex-1">
                      <span className="text-sm font-medium text-gray-900">{w.name}</span>
                      {w.role && (
                        <span className="block text-xs text-gray-500">{w.role.toLowerCase()}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className="mb-6">
            <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
              Permissions
            </h2>
            <div className="space-y-2">
              {requestedScopes.map((s) => (
                <label
                  key={s}
                  className="flex items-start gap-3 px-3 py-2 border border-gray-200 rounded-md cursor-pointer hover:border-gray-300"
                >
                  <input
                    type="checkbox"
                    checked={grantedScopes.has(s)}
                    onChange={(e) => {
                      const next = new Set(grantedScopes);
                      if (e.target.checked) next.add(s);
                      else next.delete(s);
                      setGrantedScopes(next);
                    }}
                    className="mt-0.5 text-gray-900 focus:ring-gray-300"
                  />
                  <span className="flex-1">
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {s} access
                    </span>
                    <span className="block text-xs text-gray-600 mt-0.5">
                      {SCOPE_DESCRIPTIONS[s] ?? 'Unknown scope.'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* Marked so the cookie banner docks at the top on a screen too short
              to show both (CookieConsent). The spacing above is kept tight so a
              1440x789 laptop shows Approve clear of the docked-bottom banner. */}
          <div className="flex gap-3" data-cookie-avoid>
            <button
              type="button"
              onClick={() => submit('deny')}
              disabled={status === 'submitting' || status === 'done'}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Deny
            </button>
            <button
              type="button"
              onClick={() => submit('approve')}
              disabled={
                status === 'submitting' ||
                status === 'done' ||
                !workspaceId ||
                grantedScopes.size === 0
              }
              className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            >
              {status === 'submitting' ? 'Approving...' : 'Approve'}
            </button>
          </div>

          <p className="mt-6 text-xs text-gray-500 leading-relaxed">
            BuzzBit X uses OAuth 2.0 with rotating refresh tokens. You can revoke
            access at any time from your{' '}
            <Link to="/settings/integrations" className="underline text-gray-700">
              integrations settings
            </Link>
            .
          </p>
      </AuthFormCard>
    </AuthPageLayout>
  );
};

export default Consent;
